import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Check, ChevronLeft, Star } from "lucide-react";
import { API } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const KEY_STORAGE = "akin_admin_key";

function makeClient(key) {
  return axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json", "X-Admin-Key": key },
  });
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem(KEY_STORAGE);
    if (saved) {
      verify(saved);
    }
    // eslint-disable-next-line
  }, []);

  const verify = async (k) => {
    setLoading(true);
    try {
      await makeClient(k).post("/admin/verify");
      localStorage.setItem(KEY_STORAGE, k);
      setKey(k);
      setAuthed(true);
      await loadAll(k);
    } catch {
      localStorage.removeItem(KEY_STORAGE);
      toast.error("Geçersiz yönetici anahtarı.");
    } finally {
      setLoading(false);
    }
  };

  const loadAll = async (k) => {
    try {
      const client = makeClient(k);
      const [a, r] = await Promise.all([
        client.get("/appointments"),
        client.get("/reviews/all"),
      ]);
      setAppointments(a.data);
      setReviews(r.data);
    } catch {
      toast.error("Veri yüklenemedi.");
    }
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAuthed(false);
    setKey("");
  };

  const updateApptStatus = async (id, status) => {
    try {
      await makeClient(key).patch(`/appointments/${id}`, { status });
      toast.success("Durum güncellendi.");
      loadAll(key);
    } catch {
      toast.error("Güncellenemedi.");
    }
  };

  const deleteAppt = async (id) => {
    if (!window.confirm("Randevu silinsin mi?")) return;
    try {
      await makeClient(key).delete(`/appointments/${id}`);
      toast.success("Silindi.");
      loadAll(key);
    } catch {
      toast.error("Silinemedi.");
    }
  };

  const approveReview = async (id) => {
    try {
      await makeClient(key).patch(`/reviews/${id}/approve`);
      toast.success("Yorum onaylandı.");
      loadAll(key);
    } catch {
      toast.error("Onaylanamadı.");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Yorum silinsin mi?")) return;
    try {
      await makeClient(key).delete(`/reviews/${id}`);
      toast.success("Silindi.");
      loadAll(key);
    } catch {
      toast.error("Silinemedi.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-8">
          <button onClick={() => navigate("/")} className="text-sm text-zinc-500 flex items-center gap-1 mb-6 hover:text-zinc-900" data-testid="back-home-btn">
            <ChevronLeft className="w-4 h-4" /> Ana sayfa
          </button>
          <h1 className="text-2xl font-black tracking-tight text-zinc-950 mb-2">Yönetici Girişi</h1>
          <p className="text-sm text-zinc-500 mb-6">Randevu ve yorumları yönetmek için anahtarınızı girin.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verify(key);
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label>Yönetici Anahtarı</Label>
              <Input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className="rounded-sm h-11"
                placeholder="••••••••"
                data-testid="admin-key-input"
              />
            </div>
            <Button type="submit" disabled={loading || !key} className="w-full h-11 bg-[#FF4500] hover:bg-[#E63E00] rounded-sm font-bold gap-2" data-testid="admin-login-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Giriş Yap
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const statusBadge = {
    pending: { label: "Bekliyor", cls: "bg-amber-100 text-amber-800" },
    confirmed: { label: "Onaylandı", cls: "bg-blue-100 text-blue-800" },
    completed: { label: "Tamamlandı", cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "İptal", cls: "bg-red-100 text-red-800" },
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-zinc-950 flex items-center justify-center rounded-sm">
              <span className="text-white font-black text-sm">A</span>
            </div>
            <div className="font-black tracking-tight">Yönetim Paneli</div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-sm" data-testid="go-home-btn">Ana Sayfa</Button>
            <Button variant="outline" onClick={logout} className="rounded-sm gap-2" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Çıkış</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList className="bg-white border border-zinc-200 rounded-sm">
            <TabsTrigger value="appointments" data-testid="tab-appointments">Randevular ({appointments.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Yorumlar ({reviews.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="space-y-3">
            {appointments.length === 0 ? (
              <div className="p-8 bg-white border border-zinc-200 text-center text-zinc-500 rounded-sm" data-testid="appointments-empty">Henüz randevu yok.</div>
            ) : (
              appointments.map((a) => (
                <div key={a.id} className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`appointment-row-${a.id}`}>
                  <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Müşteri</div>
                      <div className="font-bold text-zinc-950">{a.full_name}</div>
                      <a href={`tel:${a.phone}`} className="text-[#FF4500] font-semibold">{a.phone}</a>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Hizmet</div>
                      <div className="text-zinc-800">{a.service_type}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Tarih</div>
                      <div className="text-zinc-800">{a.appointment_date} · {a.appointment_time}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Adres</div>
                      <div className="text-zinc-800 line-clamp-2">{a.address}</div>
                    </div>
                    {a.note && (
                      <div className="sm:col-span-2 lg:col-span-4">
                        <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Not</div>
                        <div className="text-zinc-700 italic">{a.note}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`rounded-sm ${statusBadge[a.status]?.cls}`}>{statusBadge[a.status]?.label || a.status}</Badge>
                    <select value={a.status} onChange={(e) => updateApptStatus(a.id, e.target.value)} className="border border-zinc-300 rounded-sm px-2 h-8 text-sm" data-testid={`status-select-${a.id}`}>
                      <option value="pending">Bekliyor</option>
                      <option value="confirmed">Onaylandı</option>
                      <option value="completed">Tamamlandı</option>
                      <option value="cancelled">İptal</option>
                    </select>
                    <Button size="sm" variant="ghost" onClick={() => deleteAppt(a.id)} data-testid={`delete-appt-${a.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="reviews" className="space-y-3">
            {reviews.length === 0 ? (
              <div className="p-8 bg-white border border-zinc-200 text-center text-zinc-500 rounded-sm">Henüz yorum yok.</div>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`review-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-zinc-950">{r.name}</span>
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star key={i} className={`w-4 h-4 ${i <= r.rating ? "fill-[#FF4500] text-[#FF4500]" : "text-zinc-300"}`} />
                          ))}
                        </div>
                        {r.approved ? (
                          <Badge className="bg-emerald-100 text-emerald-800 rounded-sm">Onaylı</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 rounded-sm">Bekliyor</Badge>
                        )}
                      </div>
                      {r.service_used && <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold mt-1">{r.service_used}</div>}
                      <p className="mt-2 text-zinc-700">{r.comment}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!r.approved && (
                        <Button size="sm" onClick={() => approveReview(r.id)} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm gap-1" data-testid={`approve-review-${r.id}`}>
                          <Check className="w-4 h-4" /> Onayla
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
